"use client";

import { useEffect, useMemo, useState } from "react";
import { Search, UserPlus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { MemberListItem, roleBadgeClass } from "@/components/member-list";
import { cn } from "@/lib/cn";
import { useTranslation } from "@/lib/i18n";
import type { ManageMember } from "@/modules/account/manage-api";

type MemberIdentity = {
  user_id: string;
  first_name?: string | null;
  last_name?: string | null;
  username?: string | null;
  email?: string | null;
  phone?: string | null;
  is_bot?: boolean;
};

interface SelectableMemberPickerProps {
  members: ManageMember[];
  selectedIds: string[];
  onChange: (next: string[]) => void;
  title: string;
  hint: string;
  emptyText: string;
  defaultRoles?: string[];
  allowRoleFilters?: boolean;
}

const allFilterRoles = ["owner", "admin", "member", "guest"] as const;

export function getManageMemberDisplayName(member: MemberIdentity): string {
  const fullName = [member.first_name, member.last_name].filter(Boolean).join(" ").trim();
  if (fullName) return fullName;
  if ("email" in member && member.email) return member.email;
  if ("phone" in member && member.phone) return member.phone;
  return member.user_id;
}

export function getManageMemberSecondaryText(member: MemberIdentity): string | null {
  const contacts = [
    member.is_bot ? "BOT" : null,
    "username" in member && member.username ? `@${member.username}` : null,
    "email" in member ? member.email : null,
    "phone" in member ? member.phone : null,
  ].filter(Boolean);
  if (contacts.length > 0) return contacts.join(" • ");
  return member.user_id !== getManageMemberDisplayName(member) ? member.user_id : null;
}

export function getManageMemberRoleLabel(role: string, t: ReturnType<typeof useTranslation>): string {
  if (role === "owner") return t("invite.role_owner");
  if (role === "admin") return t("invite.role_admin");
  if (role === "guest") return t("invite.role_guest");
  return t("invite.role_member");
}

export function SelectableMemberPicker({
  members,
  selectedIds,
  onChange,
  title,
  hint,
  emptyText,
  defaultRoles,
  allowRoleFilters = false,
}: SelectableMemberPickerProps) {
  const t = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeRoles, setActiveRoles] = useState<string[]>(defaultRoles ?? [...allFilterRoles]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setActiveRoles(defaultRoles ?? [...allFilterRoles]);
    }
  }, [defaultRoles, open]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const activeRoleSet = useMemo(() => new Set(activeRoles), [activeRoles]);

  const sortedMembers = useMemo(
    () =>
      [...members].sort((a, b) =>
        getManageMemberDisplayName(a).localeCompare(getManageMemberDisplayName(b), "ru"),
      ),
    [members],
  );

  const selectedMembers = useMemo(
    () => sortedMembers.filter((member) => selectedSet.has(member.user_id)),
    [selectedSet, sortedMembers],
  );

  const availableMembers = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase("ru");
    return sortedMembers.filter((member) => {
      if (!activeRoleSet.has(member.role)) return false;
      if (!normalizedQuery) return true;
      const haystack = [
        getManageMemberDisplayName(member),
        getManageMemberSecondaryText(member),
        getManageMemberRoleLabel(member.role, t),
      ]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase("ru");
      return haystack.includes(normalizedQuery);
    });
  }, [activeRoleSet, query, sortedMembers, t]);

  function toggleMember(userId: string) {
    onChange(
      selectedSet.has(userId)
        ? selectedIds.filter((id) => id !== userId)
        : [...selectedIds, userId],
    );
  }

  function toggleRole(role: string) {
    setActiveRoles((current) =>
      current.includes(role)
        ? current.filter((item) => item !== role)
        : [...current, role],
    );
  }

  return (
    <>
      <div className="rounded-2xl border border-m-divider bg-m-surface-light/60 p-3">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-m-text">{title}</p>
            <p className="mt-1 text-xs text-m-text-hint">
              {selectedIds.length > 0
                ? t("links.auto_members_selected").replace("{count}", String(selectedIds.length))
                : hint}
            </p>
          </div>
          <Button type="button" variant="outline" onClick={() => setOpen(true)} className="shrink-0">
            <UserPlus className="h-4 w-4" />
            {t("links.auto_members_add")}
          </Button>
        </div>

        {selectedMembers.length === 0 ? (
          <p className="rounded-xl border border-dashed border-m-divider px-3 py-4 text-sm text-m-text-hint">
            {t("links.auto_members_none_selected")}
          </p>
        ) : (
          <div className="grid max-h-56 gap-2 overflow-y-auto">
            {selectedMembers.map((member) => {
              const secondary = getManageMemberSecondaryText(member);
              return (
                <MemberListItem
                  key={member.id}
                  name={getManageMemberDisplayName(member)}
                  role={member.role}
                  roleLabel={getManageMemberRoleLabel(member.role, t)}
                  secondaryText={secondary}
                  trailing={(
                    <button
                      type="button"
                      onClick={() => toggleMember(member.user_id)}
                      className="rounded-lg p-1.5 text-m-text-hint transition-colors hover:bg-m-error/10 hover:text-m-error"
                      aria-label={`${t("chat.members.remove")}: ${getManageMemberDisplayName(member)}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                />
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-m-text-hint" />
                <Input
                  aria-label={t("common.search")}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("common.search")}
                  className="pl-9"
                />
              </div>
            </div>

            {allowRoleFilters ? (
              <div className="rounded-xl border border-m-divider bg-m-surface-light/40 p-3">
                <p className="mb-2 text-xs font-medium text-m-text-secondary">{t("links.auto_members_filter_roles")}</p>
                <div className="flex flex-wrap gap-2">
                  {allFilterRoles.map((role) => {
                    const checked = activeRoleSet.has(role);
                    const label = getManageMemberRoleLabel(role, t);
                    return (
                      <label
                        key={role}
                        className={cn(
                          "flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors",
                          checked
                            ? "border-m-primary bg-m-primary/5 text-m-text"
                            : "border-m-divider bg-m-surface text-m-text-secondary",
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleRole(role)}
                          aria-label={label}
                        />
                        <span>{label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {members.length === 0 ? (
              <p className="text-sm text-m-text-hint">{emptyText}</p>
            ) : availableMembers.length === 0 ? (
              <p className="text-sm text-m-text-hint">{t("links.auto_members_no_results")}</p>
            ) : (
              <div className="grid max-h-[26rem] gap-2 overflow-y-auto">
                {availableMembers.map((member) => {
                  const checked = selectedSet.has(member.user_id);
                  const secondary = getManageMemberSecondaryText(member);
                  return (
                    <label
                      key={member.id}
                      className={cn(
                        "flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 transition-colors",
                        checked
                          ? "border-m-primary bg-m-primary/5"
                          : "border-m-divider bg-m-surface hover:bg-m-surface-light",
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleMember(member.user_id)}
                        aria-label={getManageMemberDisplayName(member)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium text-m-text">
                            {getManageMemberDisplayName(member)}
                          </span>
                          <span
                            className={cn(
                              "shrink-0 rounded-md px-2 py-0.5 text-[11px] font-medium",
                              roleBadgeClass(member.role),
                            )}
                          >
                            {getManageMemberRoleLabel(member.role, t)}
                          </span>
                        </span>
                        {secondary ? (
                          <span className="mt-1 block truncate text-xs text-m-text-secondary">{secondary}</span>
                        ) : null}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            <div className="flex justify-end">
              <Button type="button" onClick={() => setOpen(false)}>
                {t("common.done")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
